import React, { useRef, useState } from "react";
import { Table, Modal } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ToolTip } from "../../AbstractElements";

const formatServices = (invoice) => {
  if (!invoice.services || invoice.services.length === 0) return "No services";

  const allServices = invoice.services
    .map((service) => service.service_name)
    .filter(Boolean);

  const uniqueServices = [...new Set(allServices)];

  if (uniqueServices.length === 0) return "No services";
  if (uniqueServices.length <= 5) return uniqueServices.join(", ");

  return `${uniqueServices.slice(0, 5).join(", ")}...`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
};

const formatDateWithMonth = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

const formatStatus = (status) => {
  if (!status) return "N/A";
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const InvoiceTable = ({ invoices = [], loading = false }) => {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [modal, setModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const eyeButtonRefs = useRef({});

  const toggleTooltip = (id) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getServicesFromInvoice = (invoice) => {
    return formatServices(invoice);
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setModal(true);
  };

  return (
    <>
      <h6 className="mb-3 tw-font-semibold">{t("Patient Invoices")}</h6>
      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-center text-muted">{t("No invoices found")}</p>
      ) : (
        <Table size="sm" striped bordered responsive>
          <thead className="thead-light">
            <tr>
              <th>{t("Tests")}</th>
              <th style={{ whiteSpace: "nowrap" }}>{t("Invoice Number")}</th>
              {/* <th style={{ whiteSpace: "nowrap" }}>{t("NAKBHO Health ID")}</th> */}
              <th style={{ whiteSpace: "nowrap" }}>{t("Date")}</th>
              <th style={{ whiteSpace: "nowrap", textAlign: "center" }}>
                {t("Action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice, idx) => (
              <tr key={invoice._id || idx}>
                <td>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ maxWidth: "200px", whiteSpace: "nowrap" }}
                    title={getServicesFromInvoice(invoice)}
                  >
                    {getServicesFromInvoice(invoice)}
                  </span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {/* {invoice.patient?.health_id || "N/A"} */}
                  {invoice.invoice_number || "N/A"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatDate(invoice.invoice_date)}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button
                    className="success-action-button me-1"
                    id={`ViewToolTip-${invoice._id || idx}`}
                    ref={(el) =>
                      (eyeButtonRefs.current[invoice._id || idx] = el)
                    }
                  >
                    <i
                      className="fa fa-eye"
                      onClick={() => {
                        if (eyeButtonRefs.current[invoice._id || idx]) {
                          eyeButtonRefs.current[invoice._id || idx].blur();
                        }
                        setTooltipOpen((prev) => ({
                          ...prev,
                          [invoice._id || idx]: false,
                        }));
                        handleView(invoice);
                      }}
                      style={{ fontSize: "18px" }}
                    ></i>
                    <ToolTip
                      attrToolTip={{
                        placement: "top",
                        isOpen: tooltipOpen[invoice._id || idx] || false,
                        target: `ViewToolTip-${invoice._id || idx}`,
                        toggle: () => toggleTooltip(invoice._id || idx),
                      }}
                    >
                      <strong>{t("View")}</strong>
                    </ToolTip>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {/* Invoice Modal */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <div className="modal-header">
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="d-flex justify-content-between w-100 align-items-end">
              <div className="d-flex flex-column">
                <h5 className="mb-1 modal-title">
                  Patient Name:&nbsp;
                  <strong>{selectedInvoice?.patient?.name || "N/A"}</strong>
                  {/* <strong>{selectedInvoice?.patient?.full_name || "N/A"}</strong> */}
                </h5>
                <span>
                  NAKBHO Health ID:&nbsp;
                  <strong>
                    {selectedInvoice?.patient?.health_id?.match(/.{1,4}/g).join(" ") || "N/A"}
                  </strong>
                </span>
                <span>
                  Invoice Number:&nbsp;
                  <strong>{selectedInvoice?.invoice_number || "N/A"}</strong>
                </span>
              </div>
              <div>
                <small className="text-muted">
                  {formatDateWithMonth(selectedInvoice?.createdAt)}
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setModal(false)}
            ></button>
          </div>
        </div>

        <div className="modal-body">
          {selectedInvoice ? (
            <>
              <div className="mb-3">
                <h6 className="mb-0">{t("Invoice Details")}</h6>
              </div>

              <div className="mb-3 d-flex justify-content-between">
                <div>
                  <strong>{t("Status")}:</strong>&nbsp;
                  {formatStatus(selectedInvoice.status)}
                </div>
                <div>
                  <strong>{t("Payment Mode")}:</strong>&nbsp;
                  {selectedInvoice.payment_mode.charAt(0).toUpperCase() +
                    selectedInvoice.payment_mode.slice(1) || "N/A"}
                </div>
              </div>

              <div className="mb-3">
                <div
                  className="p-2 mb-2 text-white rounded"
                  style={{ backgroundColor: "var(--theme-default)" }}
                >
                  <h6>{t("Services")}</h6>
                </div>
                <div className="flex-wrap gap-3 ps-3 d-flex">
                  {selectedInvoice.services?.map((service, index) => (
                    <div
                      key={index}
                      className="mb-2 form-check d-flex align-items-center"
                      style={{ minWidth: "150px" }}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={true}
                        id={`service-${index}`}
                        style={{
                          backgroundColor: "var(--theme-default)",
                          borderColor: "var(--theme-default)",
                        }}
                        readOnly
                      />
                      <label
                        className="mb-0 form-check-label ms-2"
                        htmlFor={`service-${index}`}
                      >
                        {service.service_name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Payment Details Table */}
              <Table size="sm" striped bordered responsive className="mb-3">
                <thead>
                  <tr>
                    <th>{t("Total Amount")}</th>
                    <th>{t("Discount")}</th>
                    <th>{t("Net Amount")}</th>
                    <th>{t("Paid Amount")}</th>
                    <th>{t("Remaining Amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedInvoice.currency}&nbsp;
                      {selectedInvoice.total_amount || 0}
                    </td>
                    <td>
                      {selectedInvoice.discount_type === "fixed"
                        ? `${selectedInvoice.currency} ${
                            selectedInvoice.discount || 0
                          }`
                        : `${selectedInvoice.discount || 0}%`}
                    </td>
                    <td>
                      {selectedInvoice.currency}&nbsp;
                      {selectedInvoice.net_amount || 0}
                    </td>
                    <td>
                      {selectedInvoice.currency}&nbsp;
                      {selectedInvoice.paid_amount || 0}
                    </td>
                    <td>
                      {selectedInvoice.currency}&nbsp;
                      {selectedInvoice.remaining_amount || 0}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            <p className="text-center text-muted">
              {t("Loading invoice details...")}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default InvoiceTable;
